import React, { useEffect, useRef, useState } from "react";
import {
  Drawer,
  Form,
  Input,
  Select,
  Radio,
  DatePicker,
  Button,
  Divider,
  message,
  Spin,
} from "antd";
import OSS from "ali-oss";
import OssUpload from "../../OssUpload";
import {
  ArrowLeftOutlined,
  IdcardOutlined,
  PhoneOutlined,
  PlusOutlined,
  DeleteOutlined,
  CameraOutlined,
  CalendarOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  confirmIdentityFile,
  deleteIdentityFile,
  getCustomerRecord,
  registerCustomer,
  requestIdentityUploadToken,
  updateCustomerRecord,
} from "../../../api/user";

import "./UserRegister.css";

const IDENTITY_TYPES = [
  {
    value: "idCard",
    label: "身份证",
  },
  {
    value: "passport",
    label: "护照",
  },
  {
    value: "residencePermit",
    label: "居留证",
  },
  {
    value: "driverLicense",
    label: "驾驶证",
  },
  {
    value: "other",
    label: "其他证件",
  },
];

const CONTACT_TYPES = [
  {
    value: "phone",
    label: "手机号",
  },
  {
    value: "email",
    label: "邮箱",
  },
  {
    value: "wechat",
    label: "微信",
  },
  {
    value: "whatsapp",
    label: "WhatsApp",
  },
  {
    value: "telegram",
    label: "Telegram",
  },
  {
    value: "other",
    label: "其他",
  },
];

const COUNTRIES = [
  {
    value: "CN",
    label: "中国",
  },
  {
    value: "LA",
    label: "老挝",
  },
  {
    value: "VN",
    label: "越南",
  },
  {
    value: "TH",
    label: "泰国",
  },
  {
    value: "MM",
    label: "缅甸",
  },
  {
    value: "US",
    label: "美国",
  },
];

function UserRegister({
  open,
  onClose,
  onSuccess,
  mode = "create",
  customerId,
}) {
  const [form] = Form.useForm();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const preserveUploadedFilesRef = useRef(false);

  const [submitting, setSubmitting] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [identityFiles, setIdentityFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [primaryIdentityIndex, setPrimaryIdentityIndex] = useState(0);
  const [primaryContactIndex, setPrimaryContactIndex] = useState(0);

  /**
   * 每次打开时，如果是第一次打开，
   * 给表单设置默认值。
   */
  useEffect(() => {
    if (!open) return;

    preserveUploadedFilesRef.current = false;
    form.resetFields();
    setIdentityFiles([]);
    setPrimaryIdentityIndex(0);
    setPrimaryContactIndex(0);

    if (mode === "edit" && customerId) {
      let ignore = false;
      setLoadingCustomer(true);
      getCustomerRecord(customerId)
        .then(({ data }) => {
          if (ignore) return;
          const identityRecords = data.identities?.length
            ? data.identities
            : data.identity
              ? [data.identity]
              : [];
          const identities = identityRecords.length
            ? identityRecords
            : [{ type: "passport", files: [] }];
          setIdentityFiles(
            identities.map((identity) =>
              (identity.files || []).map((file) => ({
                uid: file._id,
                fileId: file._id,
                name: file.originalName,
                type: file.contentType,
                status: "done",
                url: file.signedUrl,
                isExisting: true,
              })),
            ),
          );
          setPrimaryIdentityIndex(Math.max(0, identities.findIndex((identity) => identity.primary === true)));
          setPrimaryContactIndex(Math.max(0, (data.contacts || []).findIndex((contact) => contact.primary === true)));
          form.setFieldsValue({
            user: {
              name: data.user?.name,
              englishName: data.user?.englishName,
              gender: data.user?.gender,
              birthday: data.user?.birthday ? dayjs(data.user.birthday) : null,
              nationality: data.user?.nationalities?.[0],
            },
            identities: identities.map((identity) => ({
              _id: identity._id,
              type: identity.type || "passport",
              country: identity.country,
              nationality:
                identity.nationality || data.user?.nationalities?.[0],
              number: identity.number,
              name: identity.name || data.user?.name,
              issueDate: identity.issueDate ? dayjs(identity.issueDate) : null,
              expireDate: identity.expireDate
                ? dayjs(identity.expireDate)
                : null,
              primary: identity.primary === true,
              files: (identity.files || []).map((file) => file._id),
            })),
            contacts: (data.contacts || []).map((contact) => ({
              _id: contact._id,
              type: contact.type,
              country: contact.country,
              value: contact.value,
              label: contact.label,
              primary: contact.primary === true,
            })),
          });
        })
        .catch((error) => {
          if (!ignore) {
            message.error(error.response?.data?.message || "加载客户资料失败");
            onCloseRef.current?.();
          }
        })
        .finally(() => {
          if (!ignore) setLoadingCustomer(false);
        });
      return () => {
        ignore = true;
      };
    }

    setIdentityFiles([[]]);
    form.setFieldsValue({
      user: {},
      identities: [{ type: "passport", primary: true, files: [] }],

      contacts: [
        {
          type: "phone",
          country: "LA",
          value: "",
          label: "",
          primary: true,
        },
      ],
    });
  }, [open, form, mode, customerId]);

  /**
   * 证件文件列表只在组件状态中保存原始文件和上传状态；
   * Form 中只保存确认后的 File ID。
   */
  const handleIdentityFilesChange = (identityIndex, { fileList }) => {
    if (fileList.length > 5) message.warning("每条证件最多上传 5 个附件");
    setUploadingFiles(
      fileList.some((file) => file.status === "uploading") ||
        identityFiles.some(
          (files, index) =>
            index !== identityIndex &&
            files.some((file) => file.status === "uploading"),
        ),
    );
    const nextFiles = fileList.slice(0, 5).map((file) => {
      const uploadedFile = file.response?.file;
      return {
        ...file,
        fileId: file.fileId || uploadedFile?._id,
        type: file.type || uploadedFile?.contentType,
        url: file.url || uploadedFile?.signedUrl,
        originFileObj: file.originFileObj || file,
      };
    });
    setIdentityFiles((current) =>
      current.map((files, index) =>
        index === identityIndex ? nextFiles : files,
      ),
    );
    form.setFieldValue(
      ["identities", identityIndex, "files"],
      nextFiles.filter((file) => file.fileId).map((file) => file.fileId),
    );
  };

  const uploadIdentityFile = async (file) => {
    const { data: tokenData } = await requestIdentityUploadToken([
      {
        originalName: file.name,
        contentType: file.type || "application/octet-stream",
        size: file.size,
      },
    ]);
    const uploadTarget = tokenData.uploads?.[0];
    if (!uploadTarget) throw new Error("服务器未返回上传目标");

    const client = new OSS({
      region: tokenData.region,
      bucket: tokenData.bucket,
      endpoint: tokenData.endpoint,
      secure: true,
      accessKeyId: tokenData.credentials.accessKeyId,
      accessKeySecret: tokenData.credentials.accessKeySecret,
      stsToken: tokenData.credentials.securityToken,
    });
    await client.multipartUpload(uploadTarget.objectKey, file);

    const { data } = await confirmIdentityFile({
      objectKey: uploadTarget.objectKey,
      originalName: file.name,
      purpose: "customer-identity",
    });
    return { file: data.file };
  };

  const handleRemoveIdentityFile = async (file) => {
    if (uploadingFiles || submitting) return false;

    if (file.fileId && !file.isExisting) {
      try {
        await deleteIdentityFile(file.fileId);
      } catch (error) {
        message.error(error.response?.data?.message || "删除证件文件失败");
        return false;
      }
    }

    return true;
  };

  const uploadPendingIdentityFiles = async (targetIdentityIndex) => {
    const identityRecords = form.getFieldValue("identities") || [];
    const fileIdsByIdentity = [];
    setUploadingFiles(true);
    try {
      for (
        let identityIndex = 0;
        identityIndex < identityRecords.length;
        identityIndex += 1
      ) {
        if (
          targetIdentityIndex !== undefined &&
          identityIndex !== targetIdentityIndex
        )
          continue;
        const files = identityFiles[identityIndex] || [];
        const pendingFiles = files.filter(
          (file) => !file.fileId && file.originFileObj,
        );
        const existingFileIds = files
          .filter((file) => file.fileId)
          .map((file) => file.fileId);
        if (pendingFiles.length > 5)
          throw new Error("每条证件最多上传 5 个附件");
        let uploadedIds = [];

        if (pendingFiles.length) {
          const { data: tokenData } = await requestIdentityUploadToken(
            pendingFiles.map((file) => ({
              originalName: file.name,
              contentType: file.type || "application/octet-stream",
              size: file.size,
            })),
          );
          if (
            !Array.isArray(tokenData.uploads) ||
            tokenData.uploads.length !== pendingFiles.length
          ) {
            throw new Error("服务器返回的上传目标数量不正确");
          }
          const client = new OSS({
            region: tokenData.region,
            bucket: tokenData.bucket,
            endpoint: tokenData.endpoint,
            secure: true,
            accessKeyId: tokenData.credentials.accessKeyId,
            accessKeySecret: tokenData.credentials.accessKeySecret,
            stsToken: tokenData.credentials.securityToken,
          });

          // const results = await Promise.allSettled(pendingFiles.map(async (file, fileIndex) => {
          //   const uploadTarget = tokenData.uploads[fileIndex];
          //   const updateFile = (updater) => setIdentityFiles((current) => current.map((list, index) => (
          //     index === identityIndex
          //       ? list.map((item) => item.uid === file.uid ? updater(item) : item)
          //       : list
          //   )));
          //   updateFile((item) => ({ ...item, status: "uploading", percent: 0 }));
          //   await client.multipartUpload(uploadTarget.objectKey, file.originFileObj, {
          //     progress: (percent) => updateFile((item) => ({
          //       ...item,
          //       status: "uploading",
          //       percent: Math.round(percent * 100),
          //     })),
          //   });
          //   const { data: confirmData } = await confirmIdentityFile({
          //     objectKey: uploadTarget.objectKey,
          //     originalName: file.name,
          //     purpose: "customer-identity",
          //   });
          //   updateFile((item) => ({
          //     ...item,
          //     status: "done",
          //     percent: 100,
          //     fileId: confirmData.file._id,
          //     url: confirmData.file.signedUrl,
          //   }));
          //   return confirmData.file._id;
          // }));

          const results = await Promise.allSettled(
            pendingFiles.map(async (file, fileIndex) => {
              const uploadTarget = tokenData.uploads[fileIndex];

              const updateFile = (updater) =>
                setIdentityFiles((current) =>
                  current.map((list, index) =>
                    index === identityIndex
                      ? list.map((item) =>
                          item.uid === file.uid ? updater(item) : item,
                        )
                      : list,
                  ),
                );

              let phase = "开始上传";

              try {
                updateFile((item) => ({
                  ...item,
                  status: "uploading",
                  percent: 0,
                }));

                console.log("[Identity Upload] 开始", {
                  name: file.name,
                  size: file.size,
                  objectKey: uploadTarget.objectKey,
                });

                phase = "multipartUpload";

                const uploadResult = await client.multipartUpload(
                  uploadTarget.objectKey,
                  file.originFileObj,
                  {
                    progress: (percent) =>
                      updateFile((item) => ({
                        ...item,
                        status: "uploading",
                        percent: Math.round(percent * 100),
                      })),
                  },
                );

                console.log("[Identity Upload] multipartUpload 成功", {
                  name: file.name,
                  objectKey: uploadTarget.objectKey,
                  uploadResult,
                });

                phase = "confirmIdentityFile";

                const { data: confirmData } = await confirmIdentityFile({
                  objectKey: uploadTarget.objectKey,
                  originalName: file.name,
                  purpose: "customer-identity",
                });

                console.log("[Identity Upload] confirm 成功", {
                  name: file.name,
                  fileId: confirmData?.file?._id,
                });

                updateFile((item) => ({
                  ...item,
                  status: "done",
                  percent: 100,
                  fileId: confirmData.file._id,
                  url: confirmData.file.signedUrl,
                }));

                return confirmData.file._id;
              } catch (error) {
                console.error("[Identity Upload] 失败", {
                  phase,
                  name: file.name,
                  objectKey: uploadTarget.objectKey,
                  error,
                  message: error?.message,
                  code: error?.code,
                  status: error?.status,
                  requestId: error?.requestId,
                  stack: error?.stack,
                });

                updateFile((item) => ({
                  ...item,
                  status: "error",
                }));

                throw error;
              }
            }),
          );

          const failedResults = results.filter(
            (result) => result.status === "rejected",
          );

          uploadedIds = results
            .filter((result) => result.status === "fulfilled")
            .map((result) => result.value);

          if (failedResults.length > 0) {
            console.error(
              "[Identity Upload] 失败详情",
              failedResults.map((result) => ({
                message: result.reason?.message,
                code: result.reason?.code,
                status: result.reason?.status,
                requestId: result.reason?.requestId,
                error: result.reason,
              })),
            );

            throw new Error(
              `${failedResults.length} 个文件上传失败，请查看控制台的 Identity Upload 日志`,
            );
          }

          uploadedIds = results
            .filter((result) => result.status === "fulfilled")
            .map((result) => result.value);
          const failedCount = results.length - uploadedIds.length;
          if (failedCount > 0)
            throw new Error(`${failedCount} 个文件上传失败，请重试`);
        }

        fileIdsByIdentity[identityIndex] = [...existingFileIds, ...uploadedIds];
        form.setFieldValue(
          ["identities", identityIndex, "files"],
          fileIdsByIdentity[identityIndex],
        );
      }
      return fileIdsByIdentity;
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleClose = async () => {
    if (!preserveUploadedFilesRef.current) {
      const temporaryFiles = identityFiles
        .flat()
        .filter((file) => file.fileId && !file.isExisting);
      const cleanupResults = await Promise.allSettled(
        temporaryFiles.map((file) => deleteIdentityFile(file.fileId)),
      );
      if (cleanupResults.some((result) => result.status === "rejected")) {
        message.warning("部分未保存的附件未能清理，请稍后重试");
      }
    }
    onCloseRef.current?.();
  };

  /**
   * 提交登记
   */
  const handleFinish = async (values) => {
    try {
      setSubmitting(true);

      if (uploadingFiles) {
        throw new Error("请等待证件文件上传完成");
      }

      const fileIdsByIdentity = await uploadPendingIdentityFiles();
      const formUser = values.user || {};

      const payload = {
        user: {
          ...(mode === "edit" ? {
            name: formUser.name,
            englishName: formUser.englishName,
            gender: formUser.gender,
            birthday: formUser.birthday
              ? formUser.birthday.format("YYYY-MM-DD")
              : null,
            nationalities: formUser.nationality ? [formUser.nationality] : [],
          } : {}),
        },
        identities: (values.identities || []).map((identity, index) => ({
          _id: identity._id,
          type: identity.type,
          country: identity.country,
          nationality: identity.nationality || formUser.nationality,
          number: identity.number,
          name: identity.name || (mode === "edit" ? formUser.name : ""),
          issueDate: identity.issueDate
            ? identity.issueDate.format("YYYY-MM-DD")
            : null,
          expireDate: identity.expireDate
            ? identity.expireDate.format("YYYY-MM-DD")
            : null,
          files: fileIdsByIdentity[index] || [],
          primary: index === primaryIdentityIndex,
        })),
        contacts: (values.contacts || []).map((contact, index) => ({
          ...contact,
          primary: index === primaryContactIndex,
        })),
      };

      const { data } =
        mode === "edit"
          ? await updateCustomerRecord(customerId, payload)
          : await registerCustomer(payload);
      const savedUser = data.user;
      preserveUploadedFilesRef.current = true;

      message.success(mode === "edit" ? "客户资料已更新" : "客户登记成功");

      /*
       * 非常关键：
       *
       * 不在这里 navigate。
       *
       * 由父组件决定登记成功以后做什么。
       */
      if (onSuccess) {
        await onSuccess(savedUser);
      }

      /*
       * 成功后关闭自己。
       */
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error(error);

      message.error(
        error.response?.data?.message || error?.message || "客户登记失败",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      open={open}
      placement="right"
      width="100%"
      closable={false}
      destroyOnClose={false}
      maskClosable={false}
      zIndex={1100}
      rootClassName="user-register-drawer"
      styles={{
        body: {
          padding: 0,
        },
      }}
    >
      <div className="user-register">
        {/* =========================
            App Navigation Bar
        ========================= */}

        <header className="user-register-header">
          <button
            type="button"
            className="user-register-back"
            onClick={handleClose}
            disabled={submitting || uploadingFiles || loadingCustomer}
            aria-label="返回"
          >
            <ArrowLeftOutlined />
          </button>

          <div className="user-register-title">
            {mode === "edit" ? "编辑客户资料" : "登记客户"}
          </div>

          <div className="user-register-header-placeholder" />
        </header>

        {/* =========================
            内容
        ========================= */}

        <main className="user-register-content">
          {loadingCustomer ? (
            <div className="customer-form-loading">
              <Spin size="large" />
            </div>
          ) : (
            <Form
              form={form}
              layout="vertical"
              onFinish={handleFinish}
              requiredMark={false}
              className="user-register-form"
            >
              {mode === "edit" && <section className="register-section">
                <div className="register-section-title">
                  <span className="register-section-icon">
                    <IdcardOutlined />
                  </span>
                  <span>客户基本信息</span>
                </div>
                <div className="identity-card identity-grid">
                  <Form.Item
                    name={["user", "name"]}
                    label="姓名"
                    rules={[{ required: true, message: "请输入姓名" }]}
                  >
                    <Input size="large" placeholder="请输入姓名" />
                  </Form.Item>
                  <Form.Item name={["user", "englishName"]} label="英文姓名">
                    <Input size="large" placeholder="请输入英文姓名（如有）" />
                  </Form.Item>
                  <Form.Item name={["user", "gender"]} label="性别">
                    <Radio.Group
                      className="gender-group"
                      optionType="button"
                      buttonStyle="solid"
                      options={[
                        { label: "男", value: "male" },
                        { label: "女", value: "female" },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item name={["user", "birthday"]} label="出生日期">
                    <DatePicker
                      size="large"
                      className="full-width"
                      placeholder="请选择出生日期"
                      format="YYYY-MM-DD"
                      suffixIcon={<CalendarOutlined />}
                    />
                  </Form.Item>
                  <Form.Item name={["user", "nationality"]} label="国籍">
                    <Select
                      size="large"
                      placeholder="请选择国籍"
                      options={COUNTRIES}
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </div>
              </section>}

              <section className="register-section">
                <div className="register-section-title">
                  <span className="register-section-icon">
                    <IdcardOutlined />
                  </span>
                  <span>证件信息</span>
                </div>
                <Form.List name="identities">
                  {(fields, { add, remove }) => (
                    <div className="identity-list">
                      {fields.map((field, index) => {
                        const files = identityFiles[index] || [];
                        return (
                          <div className="identity-card" key={field.key}>
                            <div className="identity-card-header">
                              <div className="identity-card-title">
                                <strong>证件 {index + 1}</strong>
                                <Button
                                  type="text"
                                  className="primary-icon-button"
                                  icon={primaryIdentityIndex === index ? <StarFilled /> : <StarOutlined />}
                                  aria-label={primaryIdentityIndex === index ? "主证件" : "设为主证件"}
                                  title={primaryIdentityIndex === index ? "主证件" : "设为主证件"}
                                  disabled={mode !== "edit"}
                                  tabIndex={mode === "edit" ? 0 : -1}
                                  onClick={() => {
                                    if (mode !== "edit") return;
                                    setPrimaryIdentityIndex(index);
                                    const records = form.getFieldValue("identities") || [];
                                    records.forEach((_, itemIndex) => form.setFieldValue(["identities", itemIndex, "primary"], itemIndex === index));
                                  }}
                                />
                              </div>
                              {fields.length > 1 && (
                                <Button
                                  danger
                                  type="text"
                                  icon={<DeleteOutlined />}
                                  disabled={submitting || uploadingFiles}
                                  onClick={async () => {
                                    const disposableFiles = files.filter(
                                      (file) => file.fileId && !file.isExisting,
                                    );
                                    const results = await Promise.allSettled(
                                      disposableFiles.map((file) =>
                                        deleteIdentityFile(file.fileId),
                                      ),
                                    );
                                    if (
                                      results.some(
                                        (result) =>
                                          result.status === "rejected",
                                      )
                                    ) {
                                      message.warning(
                                        "部分未保存附件未能清理，请稍后重试",
                                      );
                                    }
                                    setIdentityFiles((current) =>
                                      current.filter(
                                        (_, fileIndex) => fileIndex !== index,
                                      ),
                                    );
                                    remove(field.name);
                                  }}
                                >
                                  删除证件
                                </Button>
                              )}
                            </div>
                            <div className="identity-grid">
                              <Form.Item name={[field.name, "_id"]} hidden>
                                <Input />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, "type"]}
                                label="证件类型"
                                rules={[
                                  { required: true, message: "请选择证件类型" },
                                ]}
                              >
                                <Select size="large" options={IDENTITY_TYPES} />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, "number"]}
                                label="证件号码"
                                rules={[
                                  { required: true, message: "请输入证件号码" },
                                ]}
                              >
                                <Input
                                  size="large"
                                  placeholder="请输入证件号码"
                                  autoComplete="off"
                                />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, "name"]}
                                label="证件姓名"
                              >
                                <Input
                                  size="large"
                                  placeholder="默认为客户姓名"
                                />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, "nationality"]}
                                label="证件国籍"
                              >
                                <Select
                                  size="large"
                                  placeholder="请选择国籍"
                                  options={COUNTRIES}
                                  showSearch
                                  optionFilterProp="label"
                                />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, "country"]}
                                label="签发国家 / 地区"
                                rules={[
                                  {
                                    required: true,
                                    message: "请选择签发国家 / 地区",
                                  },
                                ]}
                              >
                                <Select
                                  size="large"
                                  placeholder="请选择签发国家 / 地区"
                                  options={COUNTRIES}
                                  showSearch
                                  optionFilterProp="label"
                                />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, "expireDate"]}
                                label="有效期"
                              >
                                <DatePicker
                                  size="large"
                                  className="full-width"
                                  placeholder="请选择有效期"
                                  format="YYYY-MM-DD"
                                  suffixIcon={<CalendarOutlined />}
                                />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, "issueDate"]}
                                label="签发日期"
                              >
                                <DatePicker
                                  size="large"
                                  className="full-width"
                                  placeholder="请选择签发日期"
                                  format="YYYY-MM-DD"
                                  suffixIcon={<CalendarOutlined />}
                                />
                              </Form.Item>
                            </div>
                            <Divider />
                            <div className="identity-files">
                              <div className="identity-files-title">
                                <CameraOutlined />
                                <span>证件附件</span>
                              </div>
                              <OssUpload
                                accept="image/*,.pdf"
                                multiple
                                maxCount={5}
                                fileList={files}
                                uploadFile={uploadIdentityFile}
                                onChange={(info) =>
                                  handleIdentityFilesChange(index, info)
                                }
                                onRemove={handleRemoveIdentityFile}
                                disabled={uploadingFiles || submitting}
                                className="identity-upload"
                              />
                            </div>
                          </div>
                        );
                      })}
                      <Button
                        block
                        icon={<PlusOutlined />}
                        disabled={submitting || uploadingFiles}
                        onClick={() => {
                          add({ type: "passport", primary: false, files: [] });
                          if (primaryIdentityIndex >= fields.length) setPrimaryIdentityIndex(0);
                          setIdentityFiles((current) => [...current, []]);
                        }}
                      >
                        添加证件
                      </Button>
                    </div>
                  )}
                </Form.List>
              </section>

              {/* =========================
                联系方式
            ========================= */}

              <section className="register-section contact-section">
                <div className="register-section-title">
                  <span className="register-section-icon">
                    <PhoneOutlined />
                  </span>

                  <span>联系方式</span>
                </div>

                <Form.List name="contacts">
                  {(fields, { add, remove }) => (
                    <>
                      <div className="contact-list">
                        {fields.map((field, index) => (
                          <div className="contact-item" key={field.key}>
                              <Form.Item name={[field.name, "primary"]} hidden><Input /></Form.Item>
                            <Form.Item
                              name={[field.name, "_id"]}
                              hidden
                            >
                              <Input />
                            </Form.Item>
                            <div className="contact-item-main">
                              <Form.Item
                                {...field}
                                name={[field.name, "type"]}
                                label={index === 0 ? "类型" : undefined}
                                rules={[
                                  {
                                    required: true,
                                    message: "请选择联系方式类型",
                                  },
                                ]}
                              >
                                <Select size="large" options={CONTACT_TYPES} />
                              </Form.Item>

                              <Form.Item
                                {...field}
                                name={[field.name, "country"]}
                                label={index === 0 ? "国家 / 地区" : undefined}
                              >
                                <Select size="large" options={COUNTRIES} />
                              </Form.Item>

                              <Form.Item
                                {...field}
                                name={[field.name, "value"]}
                                label={index === 0 ? "联系方式" : undefined}
                                rules={[
                                  {
                                    required: true,
                                    message: "请输入联系方式",
                                  },
                                ]}
                              >
                                <Input
                                  size="large"
                                  placeholder="请输入联系方式"
                                />
                              </Form.Item>

                              <Form.Item
                                {...field}
                                name={[field.name, "label"]}
                                label={index === 0 ? "备注" : undefined}
                              >
                                <Input
                                  size="large"
                                  placeholder="备注（可选）"
                                />
                              </Form.Item>
                            </div>

                            <div className="contact-item-actions">
                              <Button
                                type="text"
                                className="primary-icon-button"
                                icon={primaryContactIndex === index ? <StarFilled /> : <StarOutlined />}
                                aria-label={primaryContactIndex === index ? "主要联系方式" : "设为主要联系方式"}
                                title={primaryContactIndex === index ? "主要联系方式" : "设为主要联系方式"}
                                disabled={mode !== "edit"}
                                tabIndex={mode === "edit" ? 0 : -1}
                                onClick={() => {
                                  if (mode !== "edit") return;
                                  setPrimaryContactIndex(index);
                                  const records = form.getFieldValue("contacts") || [];
                                  records.forEach((_, itemIndex) => form.setFieldValue(["contacts", itemIndex, "primary"], itemIndex === index));
                                }}
                              />
                            </div>
                            {fields.length > 1 && (
                              <button
                                type="button"
                                className="contact-delete"
                                onClick={() => remove(field.name)}
                                aria-label="删除联系方式"
                              >
                                <DeleteOutlined />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>

                      <button
                        type="button"
                        className="add-contact-button"
                        onClick={() => {
                          add({ type: "phone", country: "LA", value: "", label: "", primary: false });
                          if (primaryContactIndex >= fields.length) setPrimaryContactIndex(0);
                        }}
                      >
                        <PlusOutlined />

                        <span>添加联系方式</span>
                      </button>
                    </>
                  )}
                </Form.List>
              </section>
            </Form>
          )}
        </main>

        {/* =========================
            手机底部操作栏
        ========================= */}

        <div className="mobile-submit">
          <Button
            type="primary"
            size="large"
            block
            loading={submitting || loadingCustomer}
            disabled={loadingCustomer}
            onClick={() => form.submit()}
          >
            {mode === "edit" ? "保存修改" : "确认登记"}
          </Button>
        </div>

        {/* =========================
            桌面操作栏
        ========================= */}

        <div className="desktop-submit">
          <Button
            size="large"
            onClick={handleClose}
            disabled={submitting || loadingCustomer}
          >
            取消
          </Button>

          <Button
            type="primary"
            size="large"
            loading={submitting || loadingCustomer}
            disabled={loadingCustomer}
            onClick={() => form.submit()}
          >
            {mode === "edit" ? "保存修改" : "确认登记"}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}

/**
 * 临时创建用户方法
 *
 * 后面直接替换成你的 API 请求即可。
 */
export default UserRegister;
